import { z } from 'zod';

import type { RowStatus } from './generated/enums.js';

export const IntegrationDimensionType = {
  COST_CENTER: 'COST_CENTER',
  DEPARTMENT: 'DEPARTMENT',
  MANAGERIAL_ACCOUNT: 'MANAGERIAL_ACCOUNT',
} as const;
export type IntegrationDimensionType =
  (typeof IntegrationDimensionType)[keyof typeof IntegrationDimensionType];
export interface IntegrationDimensionMapping {
  internalId: string;
  externalId: string;
  isPrimary: boolean;
}

export interface IntegrationDimensionMappingPair {
  internalId: string;
  externalId: string;
}

export interface IntegrationDimensionMatrixIntegration {
  integrationId: string;
  name: string;
  logoUrl: string | null;
  color: string | null;
  category: string;
  mappings: IntegrationDimensionMapping[];
  supportsDimension: boolean;
}

export interface IntegrationDimensionMatrixInternalItem {
  id: string;
  name: string;
  code?: string;
  status: RowStatus;
  entityId: string;
  entityName?: string;
}

export interface IntegrationDimensionMatrixResponse {
  dimensionType: IntegrationDimensionType;
  internalItems: IntegrationDimensionMatrixInternalItem[];
  integrations: IntegrationDimensionMatrixIntegration[];
}

export interface IntegrationDimensionExternalItem {
  externalId: string;
  name: string;
  code?: string;
  active?: boolean;
  metadata?: Record<string, unknown>;
}

export interface CreateIntegrationDimensionMappingRequest {
  integrationId: string;
  internalId: string;
  externalId: string;
}

export interface SetPrimaryIntegrationDimensionMappingRequest {
  integrationId: string;
  internalId: string;
  externalId: string;
}

export interface BulkIntegrationDimensionMappingRequest {
  integrationId: string;
  mappings: IntegrationDimensionMappingPair[];
}

export interface BulkIntegrationDimensionMappingResult {
  applied: number;
  skipped: number;
  errors: string[];
}

export interface CreateIntegrationDimensionAndMapRequest {
  integrationId: string;
  externalId: string;
  entityId: string;
  name: string;
  code?: string;
}

export const DimensionReconciliationActionSchema = z.enum(['LINK', 'CREATE', 'SKIP']);

export const DimensionReconciliationSuggestionSchema = z
  .object({
    externalId: z.string(),
    action: DimensionReconciliationActionSchema,
    internalId: z.string().nullish(),
    confidence: z.number().min(0).max(1),
    reason: z.string(),
    suggestedName: z.string().nullish(),
    suggestedCode: z.string().nullish(),
  })
  .strict();

export const DimensionReconciliationResultSchema = z
  .object({
    suggestions: z.array(DimensionReconciliationSuggestionSchema),
  })
  .strict();

export type DimensionReconciliationAction = z.infer<typeof DimensionReconciliationActionSchema>;
export type DimensionReconciliationSuggestion = z.infer<
  typeof DimensionReconciliationSuggestionSchema
>;
export type DimensionReconciliationResult = z.infer<typeof DimensionReconciliationResultSchema>;

/**
 * OpenAI Structured Outputs with `strict: true` requires all keys to be listed in `required`.
 * Optional fields must be nullable to satisfy the response validator.
 */
export const DIMENSION_RECONCILIATION_JSON_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: {
    suggestions: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          externalId: { type: 'string' },
          action: { type: 'string', enum: ['LINK', 'CREATE', 'SKIP'] },
          internalId: { type: ['string', 'null'] },
          confidence: { type: 'number', minimum: 0, maximum: 1 },
          reason: { type: 'string' },
          suggestedName: { type: ['string', 'null'] },
          suggestedCode: { type: ['string', 'null'] },
        },
        required: [
          'externalId',
          'action',
          'internalId',
          'confidence',
          'reason',
          'suggestedName',
          'suggestedCode',
        ],
        additionalProperties: false,
      },
    },
  },
  required: ['suggestions'],
  additionalProperties: false,
};
